import { useCallback, useMemo } from "react";

import { COLORS } from "@/styles/colors";

import type * as Repository from "../../../_repository";
import type * as Type from "../type";

type Props = {
	// 検索日
	searchDate: string;
	// オプションリスト
	optionPmsActiveListQueryData: Repository.Query.QueryData.OptionPmsList.OptionPmsActiveListQueryData;
	// オプションカラーリスト
	optionPmsColorListQueryData: Repository.Query.QueryData.OptionPmsColorListQueryData;
	//
	viewModel: Type.ViewModel;
};
type Result = {
	viewData: Type.ViewData;
};

export const useViewDataConverter = ({
	searchDate,
	optionPmsActiveListQueryData: {
		optionPmsList: { options },
	},
	optionPmsColorListQueryData: {
		optionPmsColorList: { optionColors },
	},
	viewModel: { selectedCardID },
}: Props): Result => {
	/**
	 * オプションへの変換
	 */
	const onConvertOption = useCallback(
		(_optionID: string, optionName: string, subOptionName?: string | null) => ({
			id: _optionID,
			nameText: subOptionName ? `${optionName} | ${subOptionName}` : optionName,
			color: optionColors.find(({ optionID }) => _optionID === optionID)?.color ?? COLORS.CHILLNN_PINK[50],
		}),
		[optionColors],
	);

	/**
	 * optionID ごとにまとまるようにソート
	 */
	const sortedOptions = useMemo(() => {
		return options.sort((a, b) => a.optionID.localeCompare(b.optionID));
	}, [options]);

	return useMemo(
		() => ({
			viewData: {
				// 日付
				searchDateText: searchDate,
				// リスト件数
				listCountText: `${options.length}件`,
				// テーブル行データ
				rowDataList: sortedOptions.map((option) => ({
					option: { ...option, ...onConvertOption(option.optionID, option.optionName, option.subOptionName) },
					subOptionID: option.subOptionID ?? undefined,
					guestNameText: `${option.guestName} 様`,
					guestTelText: option.guestTel ?? "",
					/**
					 * null の場合は手動入力を表示する
					 */
					paymentMethod: option.paymentMethod ?? null,
					memo: option.memo ?? "",
					canDelete: option.canDelete,
					cardID: option.cardID,
					date: option.date,
				})),
				// テーブル行データが存在するか
				hasRowDataList: options.length > 0,
				// 更新対象のオプション
				selectedOption: options.find(({ cardID }) => cardID === selectedCardID),
			},
		}),
		[onConvertOption, options, searchDate, selectedCardID, sortedOptions],
	);
};
